import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const empty = {
  sku: "", name: "", category: "", price: 0, cost: 0, stock: 0,
  reorder_point: 10, vendor_id: "", image_url: "",
};

export default function ProductFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(empty);
  const [vendors, setVendors] = useState([]);
  const isEdit = !!initial?.id;

  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data || []));
  }, []);

  useEffect(() => {
    setForm(initial ? { ...empty, ...initial } : empty);
  }, [initial, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!form.sku || !form.name) return toast.error("SKU and name are required");
    const payload = {
      ...form,
      price: +form.price || 0,
      cost: +form.cost || 0,
      stock: +form.stock || 0,
      reorder_point: +form.reorder_point || 10,
      vendor_id: form.vendor_id || null,
    };
    try {
      if (isEdit) {
        await api.put(`/products/${initial.id}`, { ...payload, id: initial.id });
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Failed to save product");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">SKU *</Label>
              <Input data-testid="prod-input-sku" value={form.sku} onChange={set("sku")} placeholder="LDG-2001" className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Category</Label>
              <Input data-testid="prod-input-category" value={form.category || ""} onChange={set("category")} placeholder="Apparel" className="rounded-xl" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-[12px]">Name *</Label>
            <Input data-testid="prod-input-name" value={form.name} onChange={set("name")} className="rounded-xl" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Price (retail)</Label>
              <Input data-testid="prod-input-price" type="number" step="0.01" value={form.price} onChange={set("price")} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Cost (COGS)</Label>
              <Input data-testid="prod-input-cost" type="number" step="0.01" value={form.cost} onChange={set("cost")} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Stock</Label>
              <Input data-testid="prod-input-stock" type="number" value={form.stock} onChange={set("stock")} className="rounded-xl font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Reorder at</Label>
              <Input data-testid="prod-input-reorder" type="number" value={form.reorder_point} onChange={set("reorder_point")} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Vendor</Label>
              <Select value={form.vendor_id || "__none"} onValueChange={(v) => setForm({ ...form, vendor_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="prod-input-vendor" className="rounded-xl"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-[12px]">Image URL</Label>
            <Input data-testid="prod-input-image" value={form.image_url || ""} onChange={set("image_url")} placeholder="https://..." className="rounded-xl" />
          </div>
          {form.image_url && (
            <img src={form.image_url} alt="preview" className="w-20 h-20 rounded-xl object-cover border border-black/10" />
          )}
        </div>
        <DialogFooter>
          <Button data-testid="prod-save-btn" onClick={submit} className="rounded-full bg-black text-white hover:bg-black/85">
            {isEdit ? "Save changes" : "Create product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
